<template>
  <FormCard
    :title="$t('order_creation.details_step.basic_heading')"
    data-testid="order-details-basic-card"
    :is-open="isOpen"
    :completed="completed"
    :has-error="hasError"
    :show-chevron="true"
    :show-form-icons-on-open="false"
    @toggle="emit('toggle')"
    @enter-pressed="emit('enter-pressed')">
    <template #description>
      <div v-if="!isOpen">
        <p class="summary-title">{{ order.title }}</p>
        <p class="summary-subtitle">
          {{
            !order.workload_fulltime
              ? `${order.workload_min} - ${order.workload_max}%`
              : $t('order_creation.details_step.full_time')
          }}{{
            order.workload_permanent
              ? ', ' + $t('order_creation.details_step.permanent_employment')
              : ', ' + $t('order_creation.details_step.temporary_employment')
          }}
        </p>
      </div>
    </template>
    <template #form-content>
      <div class="form-grid">
        <div class="grid-col-span-2" data-testid="order-details-basic-title-input">
          <LeadInput
            v-model="order.title"
            :label="$t('order_creation.details_step.job_title')"
            :errors="errors.title"
            :placeholder="$t('order_creation.details_step.job_title_placeholder')">
            <template #hint>
              <div v-html="$t('order_creation.details_step.job_title_hint')"></div>
            </template>
          </LeadInput>
        </div>

        <div class="grid-col-span-2">
          <label class="form-label">{{ $t('order_creation.details_step.workload') }}</label>
          <div class="option-card-group">
            <div class="option-card" :class="{ selected: order.workload_fulltime }" @click="employmentType = true">
              <div class="option-icon" :class="{ selected: order.workload_fulltime }">
                <IconPercentage100 :size="24" stroke-width="1.5" />
              </div>
              <span class="option-label">{{ $t('order_creation.details_step.full_time') }}</span>
            </div>

            <div class="option-card" :class="{ selected: !order.workload_fulltime }" @click="employmentType = false">
              <div class="option-icon" :class="{ selected: !order.workload_fulltime }">
                <IconPercentage66 :size="24" stroke-width="1.5" />
              </div>
              <span class="option-label">{{ $t('order_creation.details_step.part_time') }}</span>
            </div>
          </div>
        </div>

        <template v-if="!order.workload_fulltime">
          <LeadInput
            v-model.number="order.workload_min"
            :label="$t('order_creation.details_step.workload')"
            type="number"
            placeholder="60"
            :errors="Array.isArray(errors.workload_min) ? errors.workload_min : [errors.workload_min]">
            <template #suffix>%</template>
          </LeadInput>
          <LeadInput
            v-model.number="order.workload_max"
            label="&nbsp;"
            type="number"
            placeholder="100"
            :errors="Array.isArray(errors.workload_max) ? errors.workload_max : [errors.workload_max]">
            <template #suffix>%</template>
          </LeadInput>
        </template>

        <div class="grid-col-span-2">
          <label class="toggle">
            <LeadInput v-model="order.workload_permanent" type="switch" class="w-fit align-self-start"> </LeadInput>
            <div class="d-flex flex-column">
              <p class="mb-0 switch-label">
                {{
                  order.workload_permanent
                    ? $t('order_creation.details_step.permanent_employment')
                    : $t('order_creation.details_step.temporary_employment')
                }}
              </p>
              <p class="switch-hint">
                {{
                  order.workload_permanent
                    ? $t('order_creation.details_step.permanent_employment_description')
                    : $t('order_creation.details_step.temporary_employment_description')
                }}
              </p>
            </div>
          </label>
        </div>
      </div>
      <div class="form-actions">
        <LeadButton
          data-testid="order-details-basic-save-button"
          @click="emit('save')"
          variant="secondary"
          :loading="saving"
          >{{ $t('btn.save_and_continue') }}
          <template #icon>
            <IconCheck :size="16" stroke-width="2" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { IconCheck, IconPercentage100, IconPercentage66 } from '@tabler/icons-vue';
import FormCard from '@/components/orders/form_cards/FormCard.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import { useOrderCreationStore } from '@/stores/orderCreation';

withDefaults(
  defineProps<{
    isOpen: boolean;
    completed: boolean;
    hasError: boolean;
    errors: Record<string, any>;
    saving?: boolean;
  }>(),
  {
    saving: false,
  }
);

const emit = defineEmits<{
  toggle: [];
  save: [];
  'enter-pressed': [];
}>();

const store = useOrderCreationStore();
const { order } = storeToRefs(store);

// Setter also pins workload to 100/100 so the summary reads correctly when
// switching from part-time back to full-time.
const employmentType = computed({
  get: () => !!order.value.workload_fulltime,
  set: (val: boolean) => {
    order.value.workload_fulltime = val;
    if (val) {
      order.value.workload_min = 100;
      order.value.workload_max = 100;
    }
  },
});
</script>

<style scoped lang="scss">
* {
  font-family: var(--font-brand);
}

.summary-title {
  font-weight: 400;
  font-size: 14px;
  color: #101828;
  margin: 0;
}

.summary-subtitle {
  font-size: 13px;
  color: #8e939d;
  margin: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.grid-col-span-2 {
  grid-column: span 2;
}

.form-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
  display: block;
}

.option-card-group {
  display: flex;
  gap: 10px;
}

.option-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 2px solid #eaecf0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background-color: #fff;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &.selected {
    border-width: 2px;
    border-color: var(--tblr-primary);
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.1);
  }

  .option-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000000;
  }

  .option-label {
    font-weight: 500;
    font-size: 14px;
    color: #000000;
  }
}

.toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 10px;
  cursor: pointer;
}

.switch-label {
  font-weight: 600;
}

.switch-hint {
  font-size: 13px;
  color: #8e939d;
}

:deep(.form-check-input) {
  width: 48px !important;
  height: 24px !important;
  background-size: 22px;
}

:deep(.form-check-input:checked) {
  background-color: var(--tblr-primary);
}

.form-actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-start;
}
</style>
